import {
    eSketchPathType,
    Kb3dManager,
    KbVector,
    SketchControlPoint,
    SketchNode,
    SketchPath,
    Token,
} from '@models/classes';
import {
    CommandA,
    CommandC,
    CommandH,
    CommandL,
    CommandM,
    CommandQ,
    CommandS,
    CommandT,
    CommandV,
} from 'svg-pathdata/lib/types';
import { PathParser } from './path-parser';
import { projectOnNodeAxis } from './util';

export interface Layer {
    segments: Segment[];
    boundingbox: number[];
    holechildren: any[];
    isholepath: boolean;
}

export interface Segment {
    type: 'L' | 'M' | 'C' | 'S' | 'Q' | 'T' | 'Z';
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    x3?: number;
    y3?: number;
}

export class PathHandler {
    private pathParser: PathParser;
    private nodeManager: Kb3dManager;
    private _currentPosition: KbVector | undefined;
    private initialPosition = new KbVector(0, 0, 0);

    get currentPosition() {
        return this._currentPosition || this.initialPosition;
    }

    readonly stubPath = { id: 'stub' } as SketchPath;

    constructor(private sketchNode: SketchNode, private scaleDivider: number, nodeManager: Kb3dManager) {
        this.nodeManager = nodeManager;
    }

    parseTrace(commands: Segment[]) {
        const newPaths: SketchPath[] = [];
        if (!this.sketchNode || commands.length === 0) {
            return [];
        }

        const first = commands[0];

        if (first.type !== 'M') {
            this.pathM(newPaths, {
                type: PathParser.MOVE_TO,
                relative: false,
                x: first.x1,
                y: first.y1,
            });
        }

        for (let i = 0; i < commands.length; i++) {
            const command = commands[i];
            const commonPathCommand = {
                relative: false,
                x: command.x2,
                y: command.y2,
                x1: command.x3 || -1,
                y1: command.y3 || -1,
            };
            switch (command.type) {
                case 'M': {
                    const pathCommand: CommandM = {
                        ...commonPathCommand,
                        type: PathParser.MOVE_TO,
                    };
                    this.pathM(newPaths, pathCommand);
                    break;
                }
                case 'L': {
                    const pathCommand: CommandL = {
                        ...commonPathCommand,
                        type: PathParser.LINE_TO,
                    };
                    this.pathL(newPaths, pathCommand);
                    break;
                }
                case 'Q': {
                    if (!command.x3 || !command.y3) {
                        console.warn('Q command without x3 or y3');
                    } else {
                        const pathCommand: CommandQ = {
                            ...commonPathCommand,
                            type: PathParser.QUAD_TO,
                            x: command.x3,
                            y: command.y3,
                            x1: command.x2,
                            y1: command.y2,
                        };
                        this.pathQ(newPaths, pathCommand);
                    }
                    break;
                }
                /** imagetracer does not return C, S, or T paths
                 * 
                case 'C': {
                    const pathCommand: CommandC = {
                        ...commonPathCommand,
                        type: PathParser.CURVE_TO,
                    };
                    this.pathC(newPaths, pathCommand);
                    break;
                }
                case 'S': {
                    const pathCommand: CommandS = {
                        ...commonPathCommand,
                        type: PathParser.SMOOTH_CURVE_TO,
                    };
                    this.pathS(newPaths, pathCommand);
                    break;
                }
                case 'T': {
                    const pathCommand: CommandT = {
                        ...commonPathCommand,
                        type: PathParser.SMOOTH_QUAD_TO,
                    };
                    this.pathT(newPaths, pathCommand);
                    break;
                }
                */
                case 'Z': {
                    this.pathZ(newPaths);
                }
            }
        }

        return this.reprojectPaths(newPaths.filter(path => path !== this.stubPath));
    }

    parsePath(path: string) {
        this.pathParser = new PathParser(path);
        this.pathParser.reset();

        if (!this.sketchNode) {
            return [];
        }
        const newPaths: SketchPath[] = [];

        while (!this.pathParser.isEnd()) {
            switch (this.pathParser.next().type) {
                case PathParser.MOVE_TO:
                    this.pathM(newPaths);
                    break;

                case PathParser.LINE_TO:
                    this.pathL(newPaths);
                    break;

                case PathParser.HORIZ_LINE_TO:
                    this.pathL(newPaths);
                    break;

                case PathParser.VERT_LINE_TO:
                    this.pathL(newPaths);
                    break;

                case PathParser.CURVE_TO:
                    this.pathC(newPaths);
                    break;

                case PathParser.SMOOTH_CURVE_TO:
                    this.pathS(newPaths);
                    break;

                case PathParser.QUAD_TO:
                    this.pathQ(newPaths);
                    break;

                case PathParser.SMOOTH_QUAD_TO:
                    this.pathT(newPaths);
                    break;

                case PathParser.ARC:
                    console.warn('ARC not implemented');
                    this.pathA(newPaths);
                    break;

                case PathParser.CLOSE_PATH:
                    this.pathZ(newPaths);
                    break;

                default:
            }
        }

        return this.reprojectPaths(newPaths.filter(path => path !== this.stubPath));
    }

    protected reprojectPaths(paths: SketchPath[]) {
        paths.forEach(path => {
            path.controlPoints.forEach(cp => {
                cp.position = projectOnNodeAxis(this.sketchNode, cp.position.x, cp.position.y);
                cp.direction = projectOnNodeAxis(this.sketchNode, cp.direction.x, cp.direction.y);
                if (cp.direction2) {
                    cp.direction2 = projectOnNodeAxis(this.sketchNode, cp.direction2.x, cp.direction2.y);
                }
            });
        });

        return paths;
    }

    protected checkIfNewPathNeeded(paths: SketchPath[], desiredPathType: eSketchPathType) {
        let activePath = paths[paths.length - 1];
        if (!activePath || activePath === this.stubPath || activePath.type !== desiredPathType) {
            if (activePath === this.stubPath) {
                paths.pop();
            }

            activePath = this.addNewPath(paths, desiredPathType);
            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            if (!this._currentPosition) {
                console.warn('new path created with no moveto command');
            }
            newControlPoint.position = this.currentPosition;

            activePath.controlPoints.push(newControlPoint);
            newControlPoint.direction2 = new KbVector(0, 0, 0);
        }

        return activePath;
    }

    protected pathM(paths: SketchPath[], command?: CommandM) {
        const { pathParser } = this;
        let x = 0;
        let y = 0;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandM;
        }
        if (command) {
            x = command.x / this.scaleDivider;
            y = (command.y * -1) / this.scaleDivider;

            if (command.relative && this.currentPosition) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
            }

            this._currentPosition = new KbVector(x, y, 0);
            this.initialPosition = this._currentPosition;
            paths.push(this.stubPath);
        }
    }

    protected pathL(paths: SketchPath[], command?: CommandL | CommandH | CommandV) {
        const { pathParser } = this;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandL;
        }
        if (command) {
            let x: number | undefined;
            let y: number | undefined;

            //these 2 if checks need to happen because pathL could be a horizontal or vertical line and therefore not have one of the coordinates.
            if ('x' in command) {
                x = command.x / this.scaleDivider;
            }
            if ('y' in command) {
                y = (command.y * -1) / this.scaleDivider;
            }

            const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.points);

            if (command.relative && activePath) {
                if (x !== undefined) {
                    x = x + this.currentPosition.x;
                }
                if (y !== undefined) {
                    y = y + this.currentPosition.y;
                }
            }
            if (x === undefined) {
                // V case
                x = this.currentPosition.x || 0;
            }
            if (y === undefined) {
                // H case
                y = this.currentPosition.y || 0;
            }

            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = new KbVector(x, y, 0);
            this._currentPosition = newControlPoint.position;
            activePath.controlPoints.push(newControlPoint);
        }
    }

    protected pathC(paths: SketchPath[], command?: CommandC) {
        const { pathParser } = this;
        let x = 0;
        let y = 0;
        let x1 = 0;
        let y1 = 0;
        let x2 = 0;
        let y2 = 0;
        let lastControlPoint = undefined;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandC;
        }
        if (command) {
            x = command.x / this.scaleDivider;
            y = (command.y * -1) / this.scaleDivider;
            x1 = command.x1 / this.scaleDivider;
            y1 = (command.y1 * -1) / this.scaleDivider;
            x2 = command.x2 / this.scaleDivider;
            y2 = (command.y2 * -1) / this.scaleDivider;

            const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.cubicBezier);
            lastControlPoint = activePath.controlPoints[activePath.controlPoints.length - 1];

            if (command.relative) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
                x1 = x1 + this.currentPosition.x;
                y1 = y1 + this.currentPosition.y;
                x2 = x2 + this.currentPosition.x;
                y2 = y2 + this.currentPosition.y;
            }

            if (lastControlPoint) {
                lastControlPoint.direction = new KbVector(
                    x1 - lastControlPoint.position.x,
                    y1 - lastControlPoint.position.y,
                    0
                );
            }
            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = new KbVector(x, y, 0);
            this._currentPosition = newControlPoint.position;
            newControlPoint.direction2 = new KbVector(x2 - x, y2 - y, 0);
            activePath.controlPoints.push(newControlPoint);
        }
    }

    protected pathS(paths: SketchPath[], command?: CommandS) {
        const { pathParser } = this;
        let x = 0;
        let y = 0;
        let x2 = 0;
        let y2 = 0;
        let lastControlPoint = undefined;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandS;
        }
        if (command) {
            x = command.x / this.scaleDivider;
            y = (command.y * -1) / this.scaleDivider;
            x2 = command.x2 / this.scaleDivider;
            y2 = (command.y2 * -1) / this.scaleDivider;

            const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.cubicBezier);
            lastControlPoint = activePath.controlPoints[activePath.controlPoints.length - 1];
            lastControlPoint.direction = new KbVector(0, 0, 0);

            if (command.relative) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
                x2 = x2 + this.currentPosition.x;
                y2 = y2 + this.currentPosition.y;
            }

            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = new KbVector(x, y, 0);
            if (lastControlPoint && lastControlPoint.direction2) {
                lastControlPoint.direction = lastControlPoint.direction2?.scale(-1);
                newControlPoint.direction2 = new KbVector(x2 - x, y2 - y, 0);
            }
            this._currentPosition = newControlPoint.position;
            activePath.controlPoints.push(newControlPoint);

            return activePath;
        }
    }

    protected pathQ(paths: SketchPath[], command?: CommandQ) {
        const { pathParser } = this;
        let x = 0;
        let y = 0;
        let x1 = 0;
        let y1 = 0;
        let lastControlPoint = undefined;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandQ;
        }
        if (command) {
            x = command.x / this.scaleDivider;
            y = (command.y * -1) / this.scaleDivider;
            x1 = command.x1 / this.scaleDivider;
            y1 = (command.y1 * -1) / this.scaleDivider;

            const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.quadraticBezier);
            lastControlPoint = activePath.controlPoints[activePath.controlPoints.length - 1];

            if (command.relative) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
                x1 = x1 + this.currentPosition.x;
                y1 = y1 + this.currentPosition.y;
            }

            if (lastControlPoint) {
                lastControlPoint.direction = new KbVector(
                    x1 - lastControlPoint.position.x,
                    y1 - lastControlPoint.position.y,
                    0
                );
            }

            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = new KbVector(x, y, 0);
            this._currentPosition = newControlPoint.position;
            activePath.controlPoints.push(newControlPoint);
        }
    }

    protected pathT(paths: SketchPath[], command?: CommandT) {
        const { pathParser } = this;
        let x = 0;
        let y = 0;
        let lastControlPoint = undefined;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandT;
        }
        if (command) {
            x = command.x / this.scaleDivider;
            y = (command.y * -1) / this.scaleDivider;

            const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.quadraticBezier);
            lastControlPoint = activePath.controlPoints[activePath.controlPoints.length - 1];
            lastControlPoint.direction = new KbVector(0, 0, 0);

            if (command.relative) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
            }

            const secondLastControlPoint = activePath.controlPoints[activePath.controlPoints.length - 2];
            const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
            newControlPoint.position = new KbVector(x, y, 0);
            this._currentPosition = newControlPoint.position;
            activePath.controlPoints.push(newControlPoint);
            if (lastControlPoint && secondLastControlPoint && secondLastControlPoint) {
                lastControlPoint.direction = this.calculateReflection(
                    secondLastControlPoint.direction,
                    secondLastControlPoint,
                    lastControlPoint
                );
            }
        }
    }

    //Path A needs further implementing
    protected pathA(paths: SketchPath[], command?: CommandA) {
        const { pathParser } = this;

        if (!command && pathParser.command) {
            command = pathParser.command as CommandA;
        }
        if (command) {
            // const cX = command.cX;
            // const cY = command.cY;
            // const rX = command.rX;
            // const rY = command.rY;
            let x = command.x;
            let y = command.y;
            if (command.relative) {
                x = x + this.currentPosition.x;
                y = y + this.currentPosition.y;
            }
            // const xAxisRotation = command.xRot;
            // const largeArcFlag = command.lArcFlag;
            // const sweepFlag = command.sweepFlag;

            this._currentPosition = new KbVector(x, y, 0);
        }
    }

    protected pathZ(paths: SketchPath[]) {
        const activePath = this.checkIfNewPathNeeded(paths, eSketchPathType.points);

        const x = this.initialPosition.x;
        const y = this.initialPosition.y;

        const newControlPoint = this.nodeManager.create(Token.SketchControlPoint);
        newControlPoint.position = new KbVector(x, y, 0);
        this._currentPosition = undefined;
        activePath.controlPoints.push(newControlPoint);

        return activePath;
    }

    protected addNewPath(paths: SketchPath[], pathType?: eSketchPathType) {
        const newPath = this.nodeManager.create(Token.SketchPath);
        newPath.name = 'Path';
        newPath._expanded = true;
        paths.push(newPath);

        if (pathType) {
            newPath.type = pathType;
        }

        return newPath;
    }

    protected calculateReflection(
        vector: KbVector,
        controlPoint: SketchControlPoint,
        controlPoint2: SketchControlPoint
    ) {
        const n = controlPoint2.position.subtract(controlPoint.position).normalize();
        return vector.subtract(n.scale(2 * vector.dotProduct(n))).scale(-1);
    }
}
